import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  acceptInvitation as acceptInvitationRequest,
  acceptWarehouseJoinCode as acceptWarehouseJoinCodeRequest,
  createCompany as createCompanyRequest,
  getMe,
  login as loginRequest,
  logout as logoutRequest,
  register as registerRequest,
  switchCompany as switchCompanyRequest,
  type AuthResponse,
  type CreateCompanyInput,
  type CreateCompanyResponse,
  type LoginInput,
  type MeView,
  type RegisterInput,
} from "./auth-api";
import { clearTokens, readTokens, writeTokens } from "./auth-storage";
import { AuthContext, type AuthContextValue, type AuthStatus } from "./auth-context";

/**
 * Owns the session: hydrates from stored tokens on mount (validating them via
 * `GET /v1/me`), exposes credential/registration/logout flows, and keeps the
 * `user` view fresh across tenant switches. Token rotation on expiry is handled
 * transparently by the API client; this provider tracks only the resolved state.
 */
export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<MeView | null>(null);

  /**
   * `GET /v1/me`, and — when the account belongs to exactly one company and none
   * is active yet — switch into it before returning.
   *
   * Every fresh sign-in is issued a token with no tenant (`companyId: null`;
   * the user picks one, or creates/joins one). For an account with a single
   * company there is nothing to pick, and leaving it unselected meant an empty
   * dashboard until the user opened the switcher and chose the only option.
   *
   * Never blocks sign-in: if the switch fails (a network blip, a membership
   * that went inactive) the caller is returned as they were and lands on the
   * normal chooser, which is still a working screen. An account with several
   * companies is deliberately left alone — that choice is the user's.
   */
  const fetchMe = useCallback(async (): Promise<MeView> => {
    const me = await getMe();
    const only = me.companies.length === 1 ? me.companies[0] : undefined;
    if (me.activeCompanyId !== null || only === undefined) return me;
    try {
      writeTokens(await switchCompanyRequest(only.id));
      return await getMe();
    } catch {
      return me;
    }
  }, []);

  const loadMe = useCallback(async (): Promise<void> => {
    const me = await fetchMe();
    setUser(me);
    setStatus("authenticated");
  }, [fetchMe]);

  // Hydrate once: if tokens exist, confirm them; otherwise we are signed out.
  useEffect(() => {
    let active = true;
    if (readTokens() === null) {
      setStatus("unauthenticated");
      return;
    }
    void (async () => {
      try {
        const me = await fetchMe();
        if (active) {
          setUser(me);
          setStatus("authenticated");
        }
      } catch {
        if (active) {
          clearTokens();
          setUser(null);
          setStatus("unauthenticated");
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [fetchMe]);

  const login = useCallback(
    async (input: LoginInput): Promise<AuthResponse> => {
      const result = await loginRequest(input);
      writeTokens(result);
      await loadMe();
      return result;
    },
    [loadMe],
  );

  const register = useCallback(
    async (input: RegisterInput): Promise<AuthResponse> => {
      const result = await registerRequest(input);
      writeTokens(result);
      await loadMe();
      return result;
    },
    [loadMe],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      await logoutRequest();
    } catch {
      // Best-effort: revoke locally even if the server call fails.
    }
    clearTokens();
    setUser(null);
    setStatus("unauthenticated");
  }, []);

  const switchCompany = useCallback(
    async (companyId: string): Promise<void> => {
      const tokens = await switchCompanyRequest(companyId);
      writeTokens(tokens);
      await loadMe();
    },
    [loadMe],
  );

  const createCompany = useCallback(
    async (input: CreateCompanyInput): Promise<CreateCompanyResponse> => {
      const result = await createCompanyRequest(input);
      writeTokens(result.tokens);
      await loadMe();
      return result;
    },
    [loadMe],
  );

  const joinCompany = useCallback(
    async (code: string): Promise<void> => {
      const outcome = await acceptInvitationRequest(code);
      await switchCompany(outcome.companyId);
    },
    [switchCompany],
  );

  const joinWarehouse = useCallback(
    async (code: string): Promise<void> => {
      const outcome = await acceptWarehouseJoinCodeRequest(code);
      await switchCompany(outcome.companyId);
    },
    [switchCompany],
  );

  const reload = useCallback(async (): Promise<void> => {
    await loadMe();
  }, [loadMe]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      login,
      register,
      logout,
      switchCompany,
      createCompany,
      joinCompany,
      joinWarehouse,
      reload,
    }),
    [
      status,
      user,
      login,
      register,
      logout,
      switchCompany,
      createCompany,
      joinCompany,
      joinWarehouse,
      reload,
    ],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}
